import Page from "components/Page";
import Workspace from "components/Workspace";
import Tr from "translations/Translations";
import React, {useEffect, useMemo, useState} from "react";
import SearchBar from "./components/AuditListSearchBar";
import SimpleTable from "components/complex/simpleTable/SimpleTable";
import Pagination from "components/Pagination";
import useSearch from "hooks/useSearch";
import AuditTaskService from "./service/AuditTaskService";
import moment from "moment";
import DateFormats from "constants/DateFormats";
import WorkableIconForwardButton from "components/button/WorkableIconForwardButton";
import WorkableIconDeleteButton from "components/button/WorkableIconDeleteButton";
import WorkableIconDuplicateButton from "components/button/WorkableIconDuplicateButton";
import {connect} from "react-redux";
import { VALUES, LABELS } from "./constants/AuditTaskStatus";
import {withStyles} from "@material-ui/core";
import {withRouter} from "react-router";
import Confirm from "components/popup/confirm/Confirm";
import RouterPath, {createPath} from "libs/RouterPath";
import Button from "components/Button";
import GridBlock from "components/GridBlock";
import AddIcon from '@material-ui/icons/Add';
import WorkableIconPreviewButton from "components/button/WorkableIconPreviewButton";
import sendPost from "libs/redirectUsingPOST";
import WorkableIconTurnOffButton from "components/button/WorkableIconTurnOffButton";

const style = theme => ({
    root: {
        padding: theme.spacing(2)
    },
    searchGrid: {
        '&:nth-child(1)': {
            width: 'calc(100% - 250px)'
        },
        '&:nth-child(2)': {
            width: '250px'
        }
    },
    userColumn: {
        width: 370,
        wordBreak: 'normal'
    },
    dateColumn: {
        width: 150,
        textAlign: "center"
    },
    statusColumn: {
        width: 100,
    },
    button: {
        width: 50,
        padding: '0 !important',
    },
    gridNew: {
        padding: theme.spacing(0.5)
    },
    icon: {
        marginRight: theme.spacing(1)
    },
    buttonNew: {
        marginRight: theme.spacing(1)
    },
    cell: {
        padding: theme.spacing(1),
    }
})

function AuditList ({
        auditList = {},
        classes,
        adminMode = false
    }) {

    const { items } = auditList;

    const [loading, setLoading] = useState(false);
    const [itemForDelete, setItemForDelete] = useState(null);
    const [searchState, setSearchState] = useState(0);
    const searchInitValues = {name: '', user: '', status: '', dateStart: null, dateEnd: null}
    const sortInitValues = {column: 'identity', direction: 'desc'}

    const {searchData, totalCount, setTotalCount, setPaginationConfig, setSearchConfig, setSortConfig,}
        = useSearch({
        search: {
            filters: {...searchInitValues}
        },
        sort: {...sortInitValues}
    });

    const setFilters = (filters) => {
        const newFilters = {...searchData.search.filters, ...filters};
        setSearchConfig({...searchData.search, filters: newFilters});
    }

    useEffect(() => {
        return () => {
            AuditTaskService.clearList();
        }
    }, []);

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line
    }, [searchState])

    const fetchData = () => {
        setLoading(true);
        const {search, pagination, sort} = searchData;
        const {page, pageSize} = pagination;
        AuditTaskService.getTasks(adminMode, page, pageSize, {
            ...search.filters,
            ...search.params,
        }, sort).then(x => {
            setTotalCount(x.totalCount);
            setLoading(false);
        });
    }

    const doSearch = () => {
        setSearchState(searchState + 1);
    }

    const handleSortChange = (id) => {
        let direction = 'desc';
        const active = searchData.sort.column === id;
        if (active) {
            direction = searchData.sort.direction === 'asc' ? 'desc' : 'asc';
        }

        setSortConfig({
            column: id,
            direction
        })
        doSearch();
    }

    const getSortProps = (id) => {
        return {
            active: searchData.sort.column === id,
            direction: searchData.sort.direction,
            onClick: () => {
                handleSortChange(id)
            }
        }
    }

    const getHeaders = () => {
        if (!items) return;
        const headers = [];
        headers.push(Tr('audit_task_name'));
        if (adminMode)
            headers.push(Tr('audit_task_users'));
        headers.push({
            value: Tr('audit_task_date_start'),
            sort: getSortProps('startDate')
        });
        headers.push({
            value: Tr('audit_task_date_end'),
            sort: getSortProps('endDate')
        });
        if (adminMode) {
            headers.push({
                value: Tr('audit_task_status'),
                sort: getSortProps('status')
            });
            headers.push('');
            headers.push('');
        }
        headers.push('');
        if (!adminMode)
            headers.push('');
        return headers;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const headers = useMemo(getHeaders, [items]);

    const getHeaderClasses = () => {
        if (!items) return;
        const headers = [];
        headers.push(undefined);
        if (adminMode)
            headers.push(classes.userColumn);
        headers.push(classes.dateColumn);
        headers.push(classes.dateColumn);
        if (adminMode) {
            headers.push(classes.statusColumn);
            headers.push(classes.button);
            headers.push(classes.button);
        }
        headers.push(classes.button);
        if (!adminMode)
            headers.push(classes.button);
        return headers;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const headerClasses = useMemo(getHeaderClasses, [items]);

    const handleEnterAuditTask = (taskId) => {
        const path = createPath(adminMode ? RouterPath.audit.AUDIT_TASK_ADMIN : RouterPath.audit.AUDIT_TASK, {id: taskId});
        RouterPath.redirect(path);
    }

    const handleStartStopAuditTask = (url) => {
        sendPost(url);
    }

    const handleDeleteAuditTask = () => {
        const tmpItemId = itemForDelete.identity;
        setItemForDelete(null);
        setLoading(true);
        AuditTaskService.deleteTask(tmpItemId).then(() => fetchData());
    }

    const handleCloneAuditTask = (taskId) => {
        setLoading(true);
        AuditTaskService.cloneTask(taskId).then(r => {
            setLoading(false);
            if (r)
                handleEnterAuditTask(r.result)
        });
    }

    const getRows = () => {
        if (!items) return;
        const rows = items.map((item) => {
            const row = [];
            row.push(item.name);
            if (adminMode)
                row.push(item.auditUsers.map((u, index) => (<React.Fragment key={index}>{u}<br/></React.Fragment>)));
            row.push(item.startDate ? moment(item.startDate).format(DateFormats.DATETIME_FORMAT) : "--");
            row.push(item.endDate ? moment(item.endDate).format(DateFormats.DATETIME_FORMAT) : "--");
            if (adminMode) {
                row.push(LABELS[item.status]);
                row.push(VALUES.PREPARING === item.status ? <WorkableIconDeleteButton onClick={()=> setItemForDelete(item)} /> : '');
                row.push(<WorkableIconDuplicateButton onClick={()=> handleCloneAuditTask(item.identity)} />);
            }
            row.push(<WorkableIconPreviewButton onClick={()=> handleEnterAuditTask(item.identity)} />);
            if (!adminMode)
                if (!!item.startUrl)
                    row.push(item.status === VALUES.ACTIVE ? (<WorkableIconForwardButton tooltip={Tr('audit_task_auditor_session_start')} onClick={()=> handleStartStopAuditTask(item.startUrl)} />) : '');
                else if (!!item.stopUrl)
                    row.push(<WorkableIconTurnOffButton tooltip={Tr('audit_task_auditor_session_stop')} onClick={()=> handleStartStopAuditTask(item.stopUrl)} />);
                else
                    row.push('')
            return row;
        });
        return rows;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const rows = useMemo(getRows, [items, searchState]);

    const handleReset = () => {
        setFilters({...searchInitValues});
        setSortConfig({...sortInitValues});
        doSearch();
    }

    const handlePaginationChange = (data) => {
        setPaginationConfig(data);
        doSearch();
    }

    return (
        <div className={classes.root}>
            <Page
                floatingWorkspace
            >
                <Workspace
                    title={Tr('audit_list')}
                >
                    <GridBlock
                        alignItems='flex-start'
                        justify='space-between'
                        itemClassName={classes.searchGrid}
                    >
                        <SearchBar
                            adminMode={adminMode}
                            filters={searchData.search.filters}
                            setFilters={setFilters}
                            onSubmit={doSearch}
                            onReset={handleReset}
                            searching={loading}
                        />
                        {adminMode && <GridBlock
                            alignItems='flex-end'
                            className={classes.gridNew}
                            justify='flex-end'
                        >
                            <Button
                                className={classes.buttonNew}
                                color={'primary'}
                                variant={undefined}
                                onClick={() => handleEnterAuditTask(0)}

                            >
                                <AddIcon className={classes.icon}/>
                                {Tr('audit_task_new')}
                            </Button>
                        </GridBlock>}
                    </GridBlock>
                    <SimpleTable
                        headers={headers}
                        items={rows}
                        columnClasses={headerClasses}
                        cellClassName={classes.cell}
                    />

                    <Pagination config={searchData.pagination} setConfig={handlePaginationChange} totalCount={totalCount}/>
                </Workspace>
                <Confirm
                    open={itemForDelete !== null}
                    onConfirm={()=> handleDeleteAuditTask(itemForDelete)}
                    onCancel={() => setItemForDelete(null)}
                    confirmText="audit_task_delete_confirm"
                    confirmText2="delete_popup_text2"
                />
            </Page>
        </div>
    )
}

export default connect((state) => ({
    auditList: state.resources.auditList
}), {})(withStyles(style)(withRouter(AuditList)));